// ═══════════════════════════════════════════════════════════════════════
//   V9.7 · Vínculos aluno ↔ responsável (guardians canônicos)
//   Um responsável, vários filhos. Vincular existente nunca duplica.
// ═══════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useState } from 'react';
import {
  Users, Search, Loader2, Link2, Unlink, MessageCircle, Mail, Pencil, Wallet,
  GraduationCap, Shield, Star, DoorOpen, AlertCircle, UserPlus, Home, Trash2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = window.location.origin;
const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const ROLES = [
  ['is_financial', 'Financeiro', Wallet, 'bg-violet-100 text-violet-800'],
  ['is_legal', 'Legal', Shield, 'bg-slate-100 text-slate-700'],
  ['is_pedagogico', 'Pedagógico', GraduationCap, 'bg-emerald-100 text-emerald-800'],
  ['is_primary', 'Contato principal', Star, 'bg-amber-100 text-amber-800'],
  ['can_pickup', 'Pode retirar', DoorOpen, 'bg-blue-100 text-blue-800'],
];

const onlyDigits = (v) => (v || '').replace(/\D/g, '');

const FIELD_LABELS = {
  name: 'Nome', cpf: 'CPF', rg: 'RG', birth_date: 'Data de nascimento', email: 'E-mail',
  cellphone: 'Celular/WhatsApp', phone: 'Telefone', zip_code: 'CEP', profession: 'Profissão',
};

// erro legível SEMPRE — aceita string, objeto, array do Pydantic ou Error
export const formatApiError = (err, fallback = 'Não foi possível concluir a operação.') => {
  const d = err?.response?.data?.detail ?? err?.detail ?? err;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((e) => {
      const f = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : null;
      const label = FIELD_LABELS[f] || f;
      return label ? `${label}: ${e?.msg || 'inválido'}` : (e?.msg || '');
    }).filter(Boolean).join(' · ') || fallback;
  }
  if (d && typeof d === 'object') {
    return d.message || d.msg || d.detail || d.error || err?.message || fallback;
  }
  return err?.message || fallback;
};

const EMPTY_FORM = {
  name: '', cpf: '', rg: '', rg_orgao: '', birth_date: '', profession: '', email: '',
  cellphone: '', phone: '', zip_code: '', address: '', address_number: '', complement: '',
  neighborhood: '', city: '', state: '', relation: 'Pai/Mãe',
};

const FORM_FIELDS = [
  ['name', 'Nome completo', 'md:col-span-2'], ['cpf', 'CPF', ''], ['rg', 'RG', ''],
  ['rg_orgao', 'Órgão emissor', ''], ['birth_date', 'Nascimento (AAAA-MM-DD)', ''],
  ['profession', 'Profissão', ''], ['email', 'E-mail', 'md:col-span-2'],
  ['cellphone', 'Celular/WhatsApp', ''], ['phone', 'Telefone', ''],
  ['relation', 'Parentesco', ''], ['zip_code', 'CEP', ''],
  ['address', 'Endereço', 'md:col-span-2'], ['address_number', 'Número', ''],
  ['complement', 'Complemento', ''], ['neighborhood', 'Bairro', ''],
  ['city', 'Cidade', ''], ['state', 'UF', ''],
];

const GuardiansLinkedList = ({ studentId, studentName, student = null, compact = false, onEditGuardian, onLoaded, scope = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [savingRole, setSavingRole] = useState('');
  const [form, setForm] = useState(null);        // null = fechado · {..} = criando/editando
  const [savingForm, setSavingForm] = useState(false);
  const [dup, setDup] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/guardians/student/${studentId}`, { headers: headers() });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || 'Falha ao carregar responsáveis');
      setData(json);
      onLoaded?.(json.items || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [studentId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const updated = event => { if (!event.detail?.studentId || event.detail.studentId === studentId) load(); };
    window.addEventListener('focus', load);
    window.addEventListener('student-guardians-updated', updated);
    return () => { window.removeEventListener('focus', load); window.removeEventListener('student-guardians-updated', updated); };
  }, [load, studentId]);

  const alunoEndereco = () => ({
    zip_code: student?.zip_code || '', address: student?.address || '',
    address_number: student?.address_number || '', complement: student?.complement || '',
    neighborhood: student?.neighborhood || '', city: student?.city || '', state: student?.state || '',
  });
  const temEnderecoAluno = !!(student?.address && student?.city);

  const buscarCep = async (cep) => {
    const d = onlyDigits(cep);
    if (d.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
      const j = await r.json();
      if (j.erro) { toast.error('CEP não encontrado.'); return; }
      setForm((f) => {
        const preenchido = f.address || f.neighborhood || f.city;
        if (preenchido && !window.confirm('Substituir o endereço já digitado pelos dados do CEP?')) return f;
        return { ...f, address: j.logradouro || f.address, neighborhood: j.bairro || f.neighborhood,
                 city: j.localidade || f.city, state: j.uf || f.state };
      });
    } catch { toast.error('Não foi possível consultar o CEP agora.'); }
  };

  const aplicarEnderecoAlunoEmTodos = async () => {
    if (!temEnderecoAluno) { toast.error('O aluno ainda não tem endereço completo.'); return; }
    const e = alunoEndereco();
    const alvo = (data?.items || []).filter((i) => i.guardian?.id);
    if (!window.confirm(`Aplicar o endereço do aluno (${e.address}, ${e.address_number} · ${e.city}/${e.state}) a ${alvo.length} responsável(is)? O endereço atual deles será substituído.`)) return;
    setBusy('endereco');
    try {
      for (const it of alvo) {
        const r = await fetch(`${API_URL}/api/guardians/${it.responsible_id}`, {
          method: 'PUT', headers: headers(), body: JSON.stringify(e),
        });
        if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))));
      }
      toast.success('Endereço do aluno aplicado aos responsáveis.');
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(''); }
  };

  const salvarForm = async () => {
    if (!form?.name?.trim()) { toast.error('Informe o nome do responsável.'); return; }
    setSavingForm(true);
    const editing = !!form.id;
    const payload = { ...form, student_id: studentId, link_existing: !!form.link_existing };
    delete payload.id; delete payload.cpf_masked;
    try {
      const r = await fetch(`${API_URL}/api/guardians${editing ? `/${form.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(payload),
      });
      const json = await r.json().catch(() => ({}));
      if (r.status === 409 && json?.detail?.existing) {
        setDup({ ...json.detail, payload });     // decisão explícita na tela, sem confirm() do navegador
        setSavingForm(false);
        return;
      }
      if (!r.ok) throw new Error(formatApiError(json));
      toast.success(editing ? 'Responsável atualizado com sucesso.'
        : json.reused ? 'Responsável existente vinculado a este aluno.'
          : 'Responsável criado e vinculado a este aluno.');
      setForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingForm(false); }
  };

  const salvarForm2 = async (payload) => {
    setDup(null);
    setSavingForm(true);
    try {
      const r = await fetch(`${API_URL}/api/guardians`, {
        method: 'POST', headers: headers(), body: JSON.stringify(payload),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(json));
      toast.success('Responsável existente vinculado a este aluno.');
      setForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingForm(false); }
  };

  const toggleRole = async (it, roleKey) => {
    const key = `${it.responsible_id}:${roleKey}`;
    setSavingRole(key);
    const payload = {
      responsible_id: it.responsible_id,
      relation: it.relation,
      ...ROLES.reduce((acc, [k]) => ({ ...acc, [k]: !!it[k] }), {}),
      [roleKey]: !it[roleKey],
    };
    // atualização otimista só na seção de responsáveis
    setData((d) => ({
      ...d,
      items: (d?.items || []).map((x) => (x.responsible_id === it.responsible_id
        ? { ...x, [roleKey]: !it[roleKey] } : x)),
    }));
    try {
      const r = await fetch(`${API_URL}/api/guardians/student/${studentId}/link`, {
        method: 'POST', headers: headers(), body: JSON.stringify(payload),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || 'Falha ao atualizar papel');
      const label = ROLES.find(([k]) => k === roleKey)?.[1] || roleKey;
      toast.success(`${it.guardian?.name}: ${label} ${!it[roleKey] ? 'definido' : 'removido'}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally { setSavingRole(''); }
  };

  const desvincular = async (rid, name) => {
    if (!window.confirm(`Desvincular ${name} deste aluno? O responsável continua cadastrado e vinculado aos outros filhos.`)) return;
    setBusy(rid);
    try {
      const r = await fetch(`${API_URL}/api/guardians/student/${studentId}/link/${rid}`,
        { method: 'DELETE', headers: headers() });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || 'Falha ao desvincular');
      toast.success('Responsável desvinculado deste aluno');
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(''); }
  };

  const excluirDefinitivo = async (rid, name) => {
    setBusy(rid);
    try {
      const dep = await fetch(`${API_URL}/api/guardians/${rid}/dependencies?student_id=${studentId}`,
        { headers: headers() }).then((r) => r.json());
      if (!dep.can_delete) {
        toast.error('Este responsável possui vínculos históricos. É possível apenas desvinculá-lo deste aluno.');
        return;
      }
      if (!window.confirm(`Excluir DEFINITIVAMENTE ${name} do sistema? Esta ação não pode ser desfeita.`)) return;
      const r = await fetch(`${API_URL}/api/guardians/${rid}?student_id=${studentId}`,
        { method: 'DELETE', headers: headers() });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || 'Falha ao excluir');
      toast.success(`${name} excluído definitivamente.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(''); }
  };

  const allItems = data?.items || [];
  const items = roleFilter === 'all'
    ? allItems
    : allItems.filter((i) => !!i[roleFilter]);

  return (
    <div className="space-y-3" data-testid={`guardians-linked-list${scope}`}>
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`guardians-filters${scope}`}>
        {[['all', 'Todos'], ...ROLES.map(([k, l]) => [k, l])].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setRoleFilter(k)}
            data-testid={`guardians-filter-${k}${scope}`}
            className={`text-[11px] font-semibold rounded-full border px-2.5 py-1 transition-colors ${
              roleFilter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50 text-slate-600'}`}>
            {label} {k === 'all' ? `(${allItems.length})` : `(${allItems.filter((i) => !!i[k]).length})`}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Responsáveis vinculados ({items.length})
        </p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" className="h-8 text-[11px] font-bold bg-violet-600 hover:bg-violet-700"
            onClick={() => { setDup(null); setForm({ ...EMPTY_FORM, is_legal: true }); }}
            data-testid={`guardians-new-btn${scope}`}>
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Novo Responsável
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[11px] font-bold"
            onClick={() => setPickerOpen(true)} data-testid={`guardians-link-existing-btn${scope}`}>
            <Link2 className="w-3.5 h-3.5 mr-1" /> Vincular Responsável Existente
          </Button>
          {temEnderecoAluno && (data?.items || []).length > 0 && (
            <Button size="sm" variant="outline" className="h-8 text-[11px] font-bold"
              disabled={busy === 'endereco'} onClick={aplicarEnderecoAlunoEmTodos}
              data-testid={`guardians-apply-address${scope}`}>
              {busy === 'endereco' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <><Home className="w-3.5 h-3.5 mr-1" /> Aplicar endereço do aluno</>}
            </Button>
          )}
        </div>
      </div>

      {form && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3.5 space-y-3"
          data-testid={`guardian-form${scope}`}>
          <p className="text-[11.5px] font-bold text-violet-800">
            {form.id ? `Editar responsável · ${form.name}` : 'Novo responsável (fica vinculado a este aluno)'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {FORM_FIELDS.map(([k, label, cls]) => (
              <div key={k} className={cls}>
                <label className="text-[10px] font-semibold uppercase text-slate-500">{label}</label>
                <Input value={form[k] || ''} className="h-8 text-[12px]"
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  onBlur={k === 'zip_code' ? (e) => buscarCep(e.target.value) : undefined}
                  data-testid={`guardian-form${scope}-${k}`} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10.5px] font-bold uppercase text-slate-500">Papéis neste aluno:</span>
            {ROLES.map(([k, label]) => (
              <label key={k} className="text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1">
                <input type="checkbox" checked={!!form[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))}
                  data-testid={`guardian-form${scope}-role-${k}`} />
                {label}
              </label>
            ))}
          </div>
          {dup && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2"
              data-testid={`guardian-dup-alert${scope}`}>
              <p className="text-[11.5px] font-bold text-amber-900">
                {dup.message} {dup.existing?.name} · CPF {dup.existing?.cpf_masked || '—'}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" className="h-7 text-[11px] font-bold bg-amber-600 hover:bg-amber-700"
                  disabled={savingForm}
                  onClick={() => salvarForm2({ ...dup.payload, link_existing: true })}
                  data-testid={`guardian-dup-link${scope}`}>Vincular cadastro existente</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                  onClick={() => setDup(null)} data-testid={`guardian-dup-cancel${scope}`}>Cancelar</Button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-8 text-[11px] font-bold bg-violet-600 hover:bg-violet-700"
              disabled={savingForm} onClick={salvarForm} data-testid={`guardian-form-save${scope}`}>
              {savingForm ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Salvar responsável'}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-[11px]" disabled={savingForm}
              onClick={() => setForm(null)} data-testid={`guardian-form-cancel${scope}`}>Cancelar</Button>
            {temEnderecoAluno && (
              <Button size="sm" variant="outline" className="h-8 text-[11px] font-bold ml-auto"
                onClick={() => setForm((f) => ({ ...f, ...alunoEndereco() }))}
                data-testid={`guardian-form-use-student-address${scope}`}>
                <Home className="w-3.5 h-3.5 mr-1" /> Usar endereço do aluno
              </Button>
            )}
          </div>
        </div>
      )}

      {loading && !data && (
        <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="guardians-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando responsáveis...
        </p>
      )}
      {data && items.length === 0 && (
        <p className="text-xs text-slate-400 italic" data-testid="guardians-empty">
          Nenhum responsável vinculado a este aluno.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {items.map((it) => {
          const g = it.guardian || {};
          const phone = g.whatsapp || g.cellphone || g.phone;
          if (it.missing) {
            return (
              <div key={it.responsible_id} className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11.5px] text-amber-900"
                data-testid={`guardian-orphan-${it.responsible_id}`}>
                <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                Vínculo aponta para um responsável que não existe mais ({it.responsible_id.slice(0, 8)}…).
              </div>
            );
          }
          return (
            <div key={it.responsible_id} className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-2"
              data-testid={`guardian-card${scope}-${it.responsible_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900 break-words" data-testid={`guardian-name${scope}-${it.responsible_id}`}>{g.name || 'Nome não informado'}</p>
                  <p className="text-[11px] text-slate-500" data-testid={`guardian-relation${scope}-${it.responsible_id}`}>
                    {it.relation} · CPF {g.cpf_masked || 'não informado'}
                  </p>
                </div>
                {it.link_status === 'legacy_only' && (
                  <span className="text-[9.5px] font-bold uppercase rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 shrink-0">
                    vínculo do cadastro
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-1" data-testid={`guardian-roles${scope}-${it.responsible_id}`}>
                {ROLES.map(([k, label, Icon, cls]) => (
                  <button key={k} type="button" disabled={savingRole === `${it.responsible_id}:${k}`}
                    onClick={() => toggleRole(it, k)}
                    title={it[k] ? `Remover papel: ${label}` : `Definir como ${label}`}
                    className={`text-[10px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1 border transition-colors disabled:opacity-50 ${
                      it[k] ? `${cls} border-transparent`
                            : 'bg-white text-slate-400 border-dashed border-slate-300 hover:text-slate-600 hover:border-slate-400'}`}
                    data-testid={`guardian-role${scope}-${it.responsible_id}-${k}`}>
                    {savingRole === `${it.responsible_id}:${k}`
                      ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                      : <Icon className="w-2.5 h-2.5" />} {label}
                  </button>
                ))}
              </div>

              {!compact && (
                <div className="text-[11px] text-slate-600 space-y-0.5">
                  {(g.birth_date || g.profession) && (
                    <p className="text-slate-500">
                      {[g.birth_date, g.profession].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {g.email && <p className="break-words" data-testid={`guardian-email${scope}-${it.responsible_id}`}>{g.email}</p>}
                  {phone && <p data-testid={`guardian-phone${scope}-${it.responsible_id}`}>{phone}</p>}
                  {(g.address || g.city) && (
                    <p className="truncate text-slate-500">
                      {[g.address, g.address_number, g.neighborhood, g.city, g.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              )}

              {(it.siblings || []).length > 0 && (
                <p className="text-[10.5px] text-slate-500" data-testid={`guardian-siblings-${it.responsible_id}`}>
                  Outros filhos: <strong>{it.siblings.map((s) => s.name).join(', ')}</strong>
                </p>
              )}

              <div className="flex items-center gap-1 pt-1 flex-wrap">
                {phone && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]"
                    onClick={() => window.open(`https://wa.me/55${onlyDigits(phone)}`, '_blank')}
                    data-testid={`guardian-wa-${it.responsible_id}`}>
                    <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                  </Button>
                )}
                {g.email && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]"
                    onClick={() => window.open(`mailto:${g.email}`)}
                    data-testid={`guardian-mail-${it.responsible_id}`}>
                    <Mail className="w-3 h-3 mr-1" /> E-mail
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[10.5px]"
                  onClick={() => setForm({ ...EMPTY_FORM, ...g, relation: it.relation,
                    ...ROLES.reduce((acc, [k]) => ({ ...acc, [k]: !!it[k] }), {}) })}
                  data-testid={`guardian-edit${scope}-${it.responsible_id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Editar
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[10.5px] text-rose-600 hover:text-rose-700 ml-auto"
                  disabled={busy === it.responsible_id}
                  onClick={() => desvincular(it.responsible_id, g.name)}
                  data-testid={`guardian-unlink-${it.responsible_id}`}>
                  {busy === it.responsible_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Unlink className="w-3 h-3 mr-1" /> Desvincular</>}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[10.5px] text-rose-700 hover:bg-rose-50"
                  disabled={busy === it.responsible_id}
                  onClick={() => excluirDefinitivo(it.responsible_id, g.name)}
                  data-testid={`guardian-delete${scope}-${it.responsible_id}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Excluir
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <GuardianPicker open={pickerOpen} onOpenChange={setPickerOpen} studentId={studentId} scope={scope}
        studentName={studentName} linkedIds={items.map((i) => i.responsible_id)}
        onLinked={() => { setPickerOpen(false); load(); }} />
    </div>
  );
};

const GuardianPicker = ({ open, onOpenChange, studentId, studentName, linkedIds = [], onLinked, scope = '' }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState('');
  const [roles, setRoles] = useState({ is_financial: false, is_legal: true, is_pedagogico: false, is_primary: false, can_pickup: false });
  const [relation, setRelation] = useState('Pai/Mãe');

  useEffect(() => {
    if (!open) { setQ(''); setItems([]); return; }
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${API_URL}/api/guardians/search?q=${encodeURIComponent(q.trim())}&student_id=${studentId}`, { headers: headers() });
        const json = await r.json();
        setItems(json.items || []);
      } catch { /* noop */ } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  const vincular = async (g) => {
    setBusy(g.id);
    try {
      const r = await fetch(`${API_URL}/api/guardians/student/${studentId}/link`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ responsible_id: g.id, relation, ...roles }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || 'Falha ao vincular');
      toast.success(json.already ? 'Vínculo atualizado' : `${g.name} vinculado a ${studentName || 'este aluno'}`);
      onLinked?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`guardian-picker-dialog${scope}`}>
        <DialogHeader>
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Vincular responsável existente
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11.5px] text-slate-500 -mt-2">
          Busque por nome, CPF, e-mail ou telefone. Para irmãos, use o MESMO responsável — nada é duplicado.
        </p>

        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
            placeholder="Nome, CPF, e-mail ou telefone..." data-testid={`guardian-picker-search${scope}`} />
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5">
          <span className="text-[10.5px] font-bold uppercase text-slate-500">Papéis do vínculo:</span>
          {ROLES.map(([k, label]) => (
            <label key={k} className="text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1">
              <input type="checkbox" checked={!!roles[k]}
                onChange={(e) => setRoles((r) => ({ ...r, [k]: e.target.checked }))}
                data-testid={`guardian-picker-role-${k}`} />
              {label}
            </label>
          ))}
          <Input value={relation} onChange={(e) => setRelation(e.target.value)}
            className="h-7 w-32 text-[11px]" placeholder="Parentesco"
            data-testid="guardian-picker-relation" />
        </div>

        <div className="max-h-[46vh] overflow-y-auto space-y-2">
          {searching && <p className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando...</p>}
          {!searching && q.trim().length >= 2 && items.length === 0 && (
            <p className="text-xs text-slate-400 italic" data-testid="guardian-picker-empty">
              Nenhum responsável encontrado. Use “Novo Responsável” para cadastrar.
            </p>
          )}
          {items.map((g) => {
            const linked = linkedIds.includes(g.id);
            return (
              <div key={g.id} className="rounded-xl border border-slate-200 p-3 flex items-center gap-3"
                data-testid={`guardian-picker-item-${g.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-900 truncate">{g.name}</p>
                  <p className="text-[11px] text-slate-500">
                    CPF {g.cpf_masked || 'não informado'}
                    {(g.whatsapp || g.cellphone || g.phone) ? ` · ${g.whatsapp || g.cellphone || g.phone}` : ''}
                    {g.email ? ` · ${g.email}` : ''}
                  </p>
                  {(g.children || []).length > 0 && (
                    <p className="text-[10.5px] text-slate-500 mt-0.5">
                      Filhos vinculados: <strong>{g.children.map((c) => c.name).join(', ')}</strong>
                    </p>
                  )}
                </div>
                {linked || g.already_linked ? (
                  <span className="text-[10.5px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 shrink-0"
                    data-testid={`guardian-picker-already-${g.id}`}>Já vinculado a este aluno</span>
                ) : (
                  <Button size="sm" className="h-8 text-[11px] font-bold bg-violet-600 hover:bg-violet-700 shrink-0"
                    disabled={busy === g.id} onClick={() => vincular(g)}
                    data-testid={`guardian-picker-link-${g.id}`}>
                    {busy === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Vincular a este aluno'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GuardiansLinkedList;
